import { DevTestCommand, type DevTestSnapshot } from "@common/devTesting";
import { Guns } from "@common/definitions/items/guns";
import { Scopes } from "@common/definitions/items/scopes";
import { DevTestPacket } from "@common/packets/devTestPacket";
import { Game } from "./game";
import { UIManager } from "./managers/uiManager";
import { DevTestClientState } from "./devTestState";
import "../scss/dev-testing.scss";

export function devTestRequested(): boolean {
    return (location.hostname === "127.0.0.1" || location.hostname === "localhost")
        && !!document.querySelector<HTMLInputElement>("#momo-admin-mode")?.checked;
}

class DevTestingUI {
    private initialized = false;
    private active = false;
    private get panel(): HTMLElement { return document.getElementById("momo-admin-panel")!; }
    private get toggle(): HTMLButtonElement { return document.getElementById("momo-admin-toggle") as HTMLButtonElement; }

    init(): void {
        if (this.initialized) return;
        this.initialized = true;
        const setOpen = (open: boolean): void => {
            this.panel.hidden = !open;
            this.toggle.setAttribute("aria-expanded", String(open));
            const focused = document.activeElement;
            if (focused instanceof HTMLElement && (this.panel.contains(focused) || focused === this.toggle)) focused.blur();
        };
        this.toggle.addEventListener("click", () => { setOpen(this.panel.hidden); this.toggle.blur(); });
        document.getElementById("momo-admin-close")!.addEventListener("click", () => setOpen(false));
        window.addEventListener("keydown", event => {
            if (event.code !== "F6" || !this.active || event.repeat || !Game.gameStarted || Game.gameOver) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            setOpen(this.panel.hidden);
        }, true);
        // Interacting with these controls must never fire the equipped weapon or move the player.
        for (const event of ["pointerdown", "pointerup", "mousedown", "mouseup", "keydown", "keyup", "wheel"]) {
            this.panel.addEventListener(event, event => event.stopPropagation());
            this.toggle.addEventListener(event, event => event.stopPropagation());
        }
        const weapons = document.getElementById("admin-weapon") as HTMLSelectElement;
        const emptyWeapon = new Option("No primary gun", "255");
        emptyWeapon.disabled = true;
        weapons.add(emptyWeapon);
        Guns.definitions.forEach((gun, index) => {
            if (gun.devItem) return;
            weapons.add(new Option(gun.name, String(index)));
        });
        const scopes = document.getElementById("admin-scope") as HTMLSelectElement;
        Scopes.definitions.forEach((scope, index) => scopes.add(new Option(scope.name, String(index))));
        this.panel.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-admin-command]").forEach(input => {
            input.addEventListener("change", () => {
                const value = input instanceof HTMLInputElement ? Number(input.checked) : Number(input.value);
                this.send(Number(input.dataset.adminCommand), value);
                input.blur();
            });
        });
        for (const [id, command] of [["admin-heal", DevTestCommand.Heal], ["admin-refill", DevTestCommand.Refill], ["admin-reduce-health", DevTestCommand.ReduceHealth]] as const) {
            document.getElementById(id)!.addEventListener("click", event => {
                this.send(command, 0);
                (event.currentTarget as HTMLButtonElement).blur();
            });
        }
    }

    private send(command: DevTestCommand, value: number): void {
        if (this.active && Game.gameStarted && !Game.gameOver && !Game.activePlayer?.dead) {
            Game.sendPacket(DevTestPacket.create({ command, value }));
        }
    }

    update(state?: DevTestSnapshot): void {
        this.init();
        this.active = !!state;
        this.toggle.hidden = !state;
        if (!state) { this.reset(); return; }
        if (DevTestClientState.unlimitedAmmo !== state.unlimitedAmmo) {
            DevTestClientState.unlimitedAmmo = state.unlimitedAmmo;
            UIManager.updateWeapons();
        }
        for (const [id, value] of [["admin-invincible", state.invincible], ["admin-ammo", state.unlimitedAmmo],
            ["admin-dash", state.unlimitedDash], ["admin-extract", state.fastExtract]] as const) {
            (document.getElementById(id) as HTMLInputElement).checked = value;
        }
        for (const [id, value] of [["admin-helmet", state.helmet], ["admin-vest", state.vest], ["admin-backpack", state.backpack],
            ["admin-scope", state.scope], ["admin-weapon", state.weapon]] as const) {
            const select = document.getElementById(id) as HTMLSelectElement;
            if (document.activeElement !== select) select.value = String(value);
        }
        document.getElementById("admin-status")!.textContent = `${state.invincible ? "Invincible" : "Damage on"} · ${state.unlimitedAmmo ? "∞ ammo" : "Normal ammo"}`;
    }

    reset(): void {
        DevTestClientState.unlimitedAmmo = false;
        this.active = false;
        this.panel.hidden = true;
        this.toggle.hidden = true;
        this.toggle.setAttribute("aria-expanded", "false");
    }
}

export const DevTestUI = new DevTestingUI();
